import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, PageHeader, Select, Input, Label, Btn } from '../components/Ui';
import { StatusBadge, FlagBadges, DuplicateBadge } from '../components/Badges';
import { Eye } from 'lucide-react';

const STATUSES = [
  '', 'received', 'archived', 'check_required', 'reviewed',
  'sent_to_accountant', 'no_pdf', 'failed', 'ignored',
];

export default function Documents({ defaultFilters }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    company_code: searchParams.get('company_code') || '',
    month: searchParams.get('month') || '',
    document_month: searchParams.get('document_month') || '',
    status: searchParams.get('status') || '',
    supplier: searchParams.get('supplier') || '',
    needs_review: searchParams.get('needs_review') || (defaultFilters?.needs_review ? 'true' : ''),
    sent_to_accountant: searchParams.get('sent_to_accountant') || '',
    has_duplicates: searchParams.get('has_duplicates') || '',
    search: searchParams.get('search') || '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) params[k] = v;
    });
    // Skip API call if month filters are partially typed (avoid 422 spam)
    const monthRe = /^\d{4}-(0[1-9]|1[0-2])$/;
    if (params.month && !monthRe.test(params.month)) {
      setLoading(false);
      return;
    }
    if (params.document_month && !monthRe.test(params.document_month)) {
      setLoading(false);
      return;
    }
    const { data } = await api.get('/api/documents', { params });
    setItems(data.items);
    setTotal(data.total);
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    api.get('/api/companies').then((r) => setCompanies(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // Refresh when navigating back to this route (e.g. from /documents/{id})
  // location.key changes on every navigation, even to the same path.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const update = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    const next = new URLSearchParams(searchParams);
    if (v) next.set(k, v);
    else next.delete(k);
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title={defaultFilters?.needs_review ? 'Por revisar' : 'Documentos'}
        subtitle={`${total} resultados`}
        testid="documents-header"
      />
      <div className="p-8">
        {/* Filters */}
        <Card testid="filters-card" className="p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <Label>Empresa</Label>
              <Select
                data-testid="filter-company"
                value={filters.company_code}
                onChange={(e) => update('company_code', e.target.value)}
              >
                <option value="">Todas</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.code}>{c.code}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Mes recepción (YYYY-MM)</Label>
              <Input
                data-testid="filter-month"
                placeholder="2026-05"
                value={filters.month}
                onChange={(e) => update('month', e.target.value)}
              />
            </div>
            <div>
              <Label>Mes factura (YYYY-MM)</Label>
              <Input
                data-testid="filter-document-month"
                placeholder="2023-11"
                value={filters.document_month}
                onChange={(e) => update('document_month', e.target.value)}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select
                data-testid="filter-status"
                value={filters.status}
                onChange={(e) => update('status', e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s || 'Todos'}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Proveedor</Label>
              <Input
                data-testid="filter-supplier"
                value={filters.supplier}
                onChange={(e) => update('supplier', e.target.value)}
              />
            </div>
            <div>
              <Label>Por revisar</Label>
              <Select
                data-testid="filter-review"
                value={filters.needs_review}
                onChange={(e) => update('needs_review', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </Select>
            </div>
            <div>
              <Label>Enviados a asesoría</Label>
              <Select
                data-testid="filter-sent"
                value={filters.sent_to_accountant}
                onChange={(e) => update('sent_to_accountant', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </Select>
            </div>
            <div>
              <Label>Duplicados</Label>
              <Select
                data-testid="filter-duplicates"
                value={filters.has_duplicates}
                onChange={(e) => update('has_duplicates', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="true">Solo duplicados</option>
                <option value="false">Sin duplicados</option>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div className="md:col-span-2">
              <Label>Buscar (asunto, proveedor, remitente, archivo, n°)</Label>
              <Input
                data-testid="filter-search"
                value={filters.search}
                onChange={(e) => update('search', e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Btn
                data-testid="filter-reset"
                variant="secondary"
                onClick={() => {
                  setFilters({
                    company_code: '', month: '', document_month: '', status: '', supplier: '',
                    needs_review: defaultFilters?.needs_review ? 'true' : '',
                    sent_to_accountant: '', has_duplicates: '', search: '',
                  });
                  setSearchParams({}, { replace: true });
                }}
              >
                Restablecer filtros
              </Btn>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card testid="documents-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface text-muted">
                <tr className="text-[10px] uppercase tracking-wide2">
                  <th className="text-left px-3 py-2 font-bold">Recibido</th>
                  <th className="text-left px-3 py-2 font-bold whitespace-nowrap">F. factura</th>
                  <th className="text-left px-3 py-2 font-bold">Emp.</th>
                  <th className="text-left px-3 py-2 font-bold">Proveedor</th>
                  <th className="text-left px-3 py-2 font-bold">Asunto</th>
                  <th className="text-left px-3 py-2 font-bold">Estado</th>
                  <th className="text-left px-3 py-2 font-bold">Flags</th>
                  <th className="text-left px-3 py-2 font-bold">Archivo</th>
                  <th className="text-right px-3 py-2 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody data-testid="documents-tbody">
                {loading && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted">Cargando…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted">Sin documentos</td></tr>
                )}
                {items.map((d) => (
                  <tr
                    key={d.id}
                    data-testid={`row-doc-${d.id}`}
                    className="border-t border-border hover:bg-surface"
                  >
                    <td className="px-3 py-2 num whitespace-nowrap">{d.received_at ? new Date(d.received_at).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2 num text-muted whitespace-nowrap">{d.document_date || '—'}</td>
                    <td className="px-3 py-2 font-mono">{d.company_code}</td>
                    <td className="px-3 py-2">{d.supplier || '—'}</td>
                    <td className="px-3 py-2 max-w-xs truncate">{d.subject || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 items-center">
                        <StatusBadge status={d.status} />
                        <DuplicateBadge count={d.duplicate_count} />
                      </div>
                    </td>
                    <td className="px-3 py-2"><FlagBadges flags={d.flags} /></td>
                    <td className="px-3 py-2 max-w-[16ch] truncate font-mono">{d.original_filename || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Btn
                        data-testid={`open-doc-${d.id}`}
                        variant="secondary"
                        size="sm"
                        onClick={() => nav(`/documents/${d.id}`)}
                      >
                        <Eye size={14} /> Abrir
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}
